/// Chuẩn hoá lỗi API (RFC9457) → message ĐÃ LOCALIZE theo ngôn ngữ user đang chọn.
///
/// Lý do: backend trả `title`/`detail` bằng tiếng Việt. Trước đây store hiển thị
/// thẳng → lộ tiếng Việt ra UI. Helper này KHÔNG dùng text backend; map theo
/// `code` (ổn định, không phụ thuộc ngôn ngữ) + HTTP status → i18n key, rồi trả
/// chuỗi đã dịch (ja/en) qua i18n.global.t.
import axios from 'axios'

import { i18n } from '@/core/i18n'

/** Dịch 1 key (cast vì t() typed strict theo MessageSchema). */
function tr(key: string): string {
  return (i18n.global.t as unknown as (k: string) => string)(key)
}

/** Backend error `code` → i18n key (message cụ thể, thân thiện). */
const CODE_KEY: Record<string, string> = {
  DIAGNOSTICS_ERROR_GROUP_NOT_FOUND: 'errDiagnosticsGroupNotFound',
  DIAGNOSTICS_OCCURRENCE_NOT_FOUND: 'errDiagnosticsOccurrenceNotFound',
  SYSTEM_SETTING_NOT_FOUND: 'errSettingNotFound',
  SYSTEM_SETTING_INVALID_VALUE: 'errSettingInvalidValue',
  SYSTEM_SETTING_MANAGED_ELSEWHERE: 'errSettingManagedElsewhere',
  CATEGORY_IN_USE: 'errCategoryInUse',
  // ALT-binding-scope: own code, because errCategoryInUse tells the user to reassign
  // assets — the wrong instruction when a zone binding is what blocks the delete.
  TAG_MODEL_NOT_FOUND: 'errTagModelNotFound',
  TAG_MODEL_DUPLICATE: 'errTagModelDuplicate',
  TAG_MODEL_IN_USE: 'errTagModelInUse',
  TAG_MODEL_LAST_ONE: 'errTagModelLastOne',
  TAG_MODEL_CONCURRENT_UPDATE: 'errTagModelConcurrentUpdate',
  // Model đang có thẻ → TÊN bất biến (BE: assertIdentityImmutable). Ba mã về SKU
  // (PREFIX_REQUIRED / SKU_LOCKED / SKU_CONFLICT) đã gỡ 2026-09-14 cùng ô nhập SKU.
  TAG_MODEL_NAME_LOCKED: 'errTagModelNameLocked',
  CATEGORY_FORBIDDEN: 'errCategoryForbidden',
  INVALID_IMAGE_TYPE: 'errInvalidImageType',
  INVALID_SVG_ICON: 'errInvalidSvgIcon',
  USER_EMAIL_DUPLICATE: 'errEmailDuplicate',
  TENANT_CODE_DUPLICATE: 'errTenantCodeDuplicate',
  TENANT_ALREADY_DELETED: 'errTenantAlreadyDeleted',
  TENANT_HAS_TRACKERS: 'errTenantHasTrackers',
  TAG_IN_USE: 'errTagInUse',
  TAG_DISPOSED: 'errTagDisposed',
  TAG_DEACTIVATED: 'errTagDeactivated',
  TAG_NOT_FOUND: 'errTagNotFound',
  TAG_IMEI_EXISTS: 'errTagImeiExists',
  TAG_ALREADY_OWNED: 'errTagAlreadyOwned',
  ZONE_IN_USE: 'errZoneInUse',
  LOAN_ACTIVE_EXISTS: 'errLoanActiveExists',
  LOAN_DUE_DATE_IN_PAST: 'errLoanDueDateInPast',
  LOAN_DUE_DATE_NOT_LATER: 'errLoanDueDateNotLater',
  TTS_ACCOUNT_EXISTS: 'errTtsAccountExists',
  ALERT_LEVEL_IN_USE: 'errAlertLevelInUse',
  MANAGER_EXISTS: 'errManagerExists',
  ALERT_ALREADY_HANDLED: 'errAlertAlreadyHandled',
  ALERT_ALREADY_ACKNOWLEDGED: 'errAlertAlreadyAcknowledged',
  ALERT_EXPORT_TOO_LARGE: 'errAlertExportTooLarge',
  AUDIT_EXPORT_TOO_LARGE: 'errAuditExportTooLarge',
  SCENARIO_SET_NOT_FOUND: 'errScenarioSetNotFound',
  SCENARIO_SET_NAME_DUPLICATE: 'errScenarioSetNameDuplicate',
  SCENARIO_SET_IN_USE: 'errScenarioSetInUse',
  SCENARIO_SET_SUPER_ADMIN_ONLY: 'errScenarioSetSuperAdminOnly',
  SCENARIO_SET_WRONG_TENANT: 'errScenarioSetWrongTenant',
  BINDING_CATEGORY_NOT_FOUND: 'errBindingCategoryNotFound',
  SCENARIO_TYPE_INVALID: 'errScenarioTypeInvalid',
  // USR-corp-admin (Quản lý Corp Admin)
  CORP_ADMIN_NOT_FOUND: 'errCorpAdminNotFound',
  CORP_ADMIN_LAST_ACTIVE_GUARD: 'errCorpAdminLastActive',
  USER_NOT_INVITED_STATUS: 'errUserNotInvitedStatus',
  CORP_ADMIN_ALREADY_DEACTIVATED: 'errCorpAdminAlreadyDeactivated',
  CORP_ADMIN_REACTIVATE_DELETED_TENANT: 'errCorpAdminReactivateDeletedTenant',
  PASSWORD_POLICY_VIOLATED: 'errPasswordPolicyViolated',
  TENANT_NOT_FOUND: 'errTenantNotFound',
  TEST_MODE_UNAVAILABLE: 'errTestModeUnavailable',
  ALERT_EXPLAIN_UNAVAILABLE: 'errAlertExplainUnavailable',
  // USR-position-history. The range codes are a backstop: the screen validates the
  // window before the round trip, so these surface only on a hand-crafted request.
  USER_POSITION_HISTORY_UNAVAILABLE: 'errUserPositionHistoryUnavailable',
  USER_POSITION_HISTORY_RANGE_INVALID: 'errUserPositionRangeInvalid',
  USER_POSITION_HISTORY_RANGE_TOO_LARGE: 'errUserPositionRangeTooLarge',
  // TTS-account-card-scope
  SUPER_ADMIN_CREATE_FORBIDDEN: 'errSuperAdminForbidden',
  TTS_TOKEN_SERVICE_FAILED: 'errTtsConnectionFailed',
  TTS_REQUEST_FAILED: 'errTtsConnectionFailed',
  TTS_ALL_ACCOUNTS_THROTTLED: 'errTtsAllThrottled',
  TTS_ACCOUNT_UNAVAILABLE: 'errTtsAllThrottled',
  TTS_USER_ID_DUPLICATE: 'errTtsUserIdDuplicate',
  TTS_CREDENTIALS_INVALID: 'errTtsCredentialsInvalid',
  TTS_ACCOUNT_NOT_IN_TENANT: 'errTtsAccountNotInTenant',
  TTS_ACCOUNT_ALREADY_DISABLED: 'errTtsAccountAlreadyDisabled',
  // Backstop: chỉ hiện nếu thiếu gate test-mode ở backend (xem tts.test-mode-gate.ts).
  TTS_TEST_MODE_BLOCKED: 'errTtsTestModeBlocked',
  // SYS-api-i18n — full error-code coverage (FR-SYS-I18N-006)
  AUTH_INVALID_CREDENTIALS: 'errInvalidCredentials',
  AUTH_ACCOUNT_LOCKED: 'errAccountLocked',
  AUTH_ACCOUNT_DEACTIVATED: 'errAccountDeactivated',
  AUTH_TENANT_DEACTIVATED: 'errTenantDeactivated',
  AUTH_ACCOUNT_NOT_ACTIVATED: 'errAccountNotActivated',
  ACCOUNT_ALREADY_ACTIVE: 'errAccountAlreadyActive',
  ACTIVATION_TOKEN_INVALID: 'errActivationTokenInvalid',
  ACTIVATION_TOKEN_EXPIRED: 'errActivationTokenExpired',
  RESET_TOKEN_INVALID: 'errResetTokenInvalid',
  RESET_TOKEN_EXPIRED: 'errResetTokenExpired',
  AUTH_TOKEN_EXPIRED: 'errAuthTokenExpired',
  AUTH_TOKEN_INVALID: 'errAuthTokenInvalid',
  AUTH_TOKEN_MISSING: 'errAuthTokenMissing',
  AUTH_REFRESH_INVALID: 'errAuthRefreshInvalid',
  AUTH_REFRESH_REUSED: 'errAuthRefreshReused',
  AUTH_REFRESH_IN_PROGRESS: 'errAuthRefreshInProgress',
  CSRF_INVALID: 'errAuthRefreshInvalid',
  DEVICE_NOT_WHITELISTED: 'errDeviceNotWhitelisted',
  IP_NOT_ALLOWED: 'errIpNotAllowed',
  ACCESS_WHITELIST_DUPLICATE: 'errAccessWhitelistDuplicate',
  // Hai mã CHẶN-TRƯỚC-KHI-KHOÁ: bật chặn mà không có gì trong allowlist (hoặc không xác
  // định được IP của chính mình) = khoá cả tenant ra ngoài. Câu chữ phải nói ra lý do,
  // vì đây là lúc người dùng vừa được dialog hứa "sẽ không mất quyền truy cập".
  ACCESS_WHITELIST_SELF_IP_UNKNOWN: 'errAccessWhitelistSelfIpUnknown',
  ACCESS_WHITELIST_EMPTY_ENABLE_BLOCKED: 'errAccessWhitelistEmptyEnableBlocked',
  ACCESS_WHITELIST_NOT_FOUND: 'errAccessWhitelistNotFound',
  PRIVATE_TIME_SETTINGS_NOT_FOUND: 'errPrivateTimeNotFound',
  RATE_LIMIT_EXCEEDED: 'errRateLimitExceeded',
  ASSET_NOT_FOUND: 'errAssetNotFound',
  TAG_IMEI_INVALID: 'errTagImeiInvalid',
  NO_TAG_ASSIGNED: 'errNoTagAssigned',
  MANAGER_NOT_FOUND: 'errManagerNotFound',
  TTS_ACCOUNT_NOT_FOUND: 'errTtsAccountNotFound',
  TTS_PARENT_NOT_FOUND: 'errTtsParentNotFound',
  TTS_ACCOUNT_REQUIRED: 'errTtsAccountRequired',
  TTS_CREDENTIALS_REQUIRED: 'errTtsCredentialsRequired',
  ORG_NOT_FOUND: 'errOrgNotFound',
  ORGANIZATION_NOT_FOUND: 'errOrgNotFound',
  PARENT_ORG_NOT_FOUND: 'errParentOrgNotFound',
  ORG_CYCLE_DETECTED: 'errOrgCycleDetected',
  ORG_MAX_DEPTH_EXCEEDED: 'errOrgMaxDepthExceeded',
  ORG_HAS_CHILDREN: 'errOrgHasChildren',
  ORG_HAS_USERS: 'errOrgHasUsers',
  ORG_ROOT_PROTECTED: 'errOrgRootProtected',
  ORG_ROOT_EXISTS: 'errOrgRootExists',
  ASSET_CODE_DUPLICATE: 'errAssetCodeDuplicate',
  LOAN_NOT_FOUND: 'errLoanNotFound',
  LOAN_RETURNED: 'errLoanReturned',
  LOAN_ALREADY_RETURNED: 'errLoanAlreadyReturned',
  ALERT_LEVEL_NOT_FOUND: 'errAlertLevelNotFound',
  ALERT_NOT_FOUND: 'errAlertNotFound',
  ALERT_NOT_SNOOZEABLE: 'errAlertNotSnoozeable',
  COMBINED_RULE_NOT_FOUND: 'errCombinedRuleNotFound',
  SCENARIO_KEY_INVALID: 'errScenarioKeyInvalid',
  BLE_STATUS_INVALID: 'errBleStatusInvalid',
  BLE_STATUS_NOT_MANAGER: 'errBleStatusNotManager',
  ZONE_NOT_FOUND: 'errZoneNotFound',
  ZONE_SHAPE_MISMATCH: 'errZoneShapeMismatch',
  APPROVAL_NOT_FOUND: 'errApprovalNotFound',
  APPROVAL_ALREADY_DECIDED: 'errApprovalAlreadyDecided',
  APPROVAL_TIME_OVERLAP: 'errApprovalTimeOverlap',
  TENANT_REQUIRED: 'errTenantRequired',
  USER_NOT_FOUND: 'errUserNotFound',
  USER_ALREADY_DEACTIVATED: 'errUserAlreadyDeactivated',
  CANNOT_DEACTIVATE_SELF: 'errCannotDeactivateSelf',
  SUPER_ADMIN_ONLY: 'errSuperAdminOnly',
  OVERSIGHT_SUPER_ADMIN_ONLY: 'errSuperAdminOnly',
  CORP_ADMIN_ONLY: 'errCorpAdminOnly',
  INVALID_RANGE: 'errInvalidRange',
  INVALID_JSON_BODY: 'errInvalidJsonBody',
  VALIDATION_ERROR: 'errValidation',
  // ORG-csv-import
  ORG_CODE_DUPLICATE: 'errOrgCodeDuplicate',
  ORG_CODE_REQUIRED: 'errOrgCodeRequired',
  ORG_CODE_TOO_LONG: 'errOrgCodeTooLong',
  ORG_CODE_INVALID_FORMAT: 'errOrgCodeInvalidFormat',
  ORG_CODE_DUPLICATE_IN_FILE: 'errOrgCodeDuplicateInFile',
  ORG_NAME_REQUIRED: 'errOrgNameRequired',
  ORG_NAME_TOO_LONG: 'errOrgNameTooLong',
  ORG_DESCRIPTION_TOO_LONG: 'errOrgDescriptionTooLong',
  ORG_PARENT_CODE_NOT_FOUND: 'errOrgParentCodeNotFound',
  ORG_PARENT_CODE_REQUIRED: 'errOrgParentCodeRequired',
  ORG_PARENT_ROW_HAS_ERROR: 'errOrgParentRowHasError',
  ORG_CYCLE_IN_FILE: 'errOrgCycleInFile',
  ORG_CSV_DEPTH_EXCEEDED: 'errOrgCsvDepthExceeded',
  ORG_CSV_TOO_MANY_ROWS: 'errOrgCsvTooManyRows',
  ORG_CSV_INVALID_HEADER: 'errOrgCsvInvalidHeader',
  ORG_CSV_EMPTY: 'errOrgCsvEmpty',
  ORG_CSV_ENCODING_ERROR: 'errOrgCsvEncoding',
  ORG_CSV_FILE_TOO_LARGE: 'errOrgCsvFileTooLarge',
  ORG_CSV_INVALID_FILE: 'errOrgCsvInvalidFile',
  ORG_CSV_HAS_ROW_ERRORS: 'errOrgCsvHasRowErrors',
  ORG_CSV_ROOT_NOT_UPDATABLE: 'errOrgCsvRootNotUpdatable',
  // AST-csv-import
  ASSET_CSV_INVALID_FILE: 'errAssetCsvInvalidFile',
  ASSET_CSV_FILE_TOO_LARGE: 'errAssetCsvFileTooLarge',
  ASSET_CSV_INVALID_HEADER: 'errAssetCsvInvalidHeader',
  ASSET_CSV_EMPTY: 'errAssetCsvEmpty',
  ASSET_CSV_TOO_MANY_ROWS: 'errAssetCsvTooManyRows',
  ASSET_CSV_HAS_ROW_ERRORS: 'errAssetCsvHasRowErrors',
  ASSET_CODE_REQUIRED: 'errAssetCodeRequired',
  ASSET_CODE_TOO_LONG: 'errAssetCodeTooLong',
  ASSET_CODE_DUPLICATE_IN_FILE: 'errAssetCodeDuplicateInFile',
  ASSET_NAME_REQUIRED: 'errAssetNameRequired',
  ASSET_NAME_TOO_LONG: 'errAssetNameTooLong',
  ASSET_CATEGORY_NOT_FOUND: 'errAssetCategoryNotFound',
  ASSET_ORG_NOT_FOUND: 'errAssetOrgNotFound',
  ASSET_TYPE_INVALID: 'errAssetTypeInvalid',
  ASSET_TRACKING_MODE_INVALID: 'errAssetTrackingModeInvalid',
  // USR-csv-import
  USER_CSV_INVALID_FILE: 'errUserCsvInvalidFile',
  USER_CSV_FILE_TOO_LARGE: 'errUserCsvFileTooLarge',
  USER_CSV_INVALID_HEADER: 'errUserCsvInvalidHeader',
  USER_CSV_EMPTY: 'errUserCsvEmpty',
  USER_CSV_TOO_MANY_ROWS: 'errUserCsvTooManyRows',
  USER_CSV_HAS_ROW_ERRORS: 'errUserCsvHasRowErrors',
  CSV_IMPORT_JOB_NOT_FOUND: 'errCsvImportJobNotFound',
  IMPORT_JOB_FAILED: 'errImportJobFailed',
  IMPORT_JOB_TYPE_UNSUPPORTED: 'errImportJobTypeUnsupported',
  USER_FULL_NAME_REQUIRED: 'errUserFullNameRequired',
  USER_FULL_NAME_TOO_LONG: 'errUserFullNameTooLong',
  USER_EMAIL_REQUIRED: 'errUserEmailRequired',
  USER_EMAIL_INVALID: 'errUserEmailInvalid',
  USER_EMAIL_DUPLICATE_IN_FILE: 'errUserEmailDuplicateInFile',
  USER_ORG_NOT_FOUND: 'errUserOrgNotFound',
  USER_ROLE_INVALID: 'errUserRoleInvalid',
  // AST-shared-loan
  ASSET_NOT_SHARED: 'errAssetNotShared',
  SHARED_ASSET_NOT_AVAILABLE: 'errSharedAssetNotAvailable',
  SHARED_ASSET_HAS_ACTIVE_LOAN: 'errSharedAssetHasActiveLoan',
  ASSET_HAS_ACTIVE_LOAN: 'errAssetHasActiveLoan',
  SHARED_ASSET_NO_MANAGER: 'errSharedAssetNoManager',
  LOAN_NOT_BORROWER: 'errLoanNotBorrower',
  LOAN_FORCE_RETURN_FORBIDDEN: 'errLoanForceReturnForbidden',
  LOAN_EXPORT_TOO_LARGE: 'errLoanExportTooLarge',
  VISIBILITY_ORG_NOT_DESCENDANT: 'errVisibilityOrgNotDescendant',
  // AST-lost-report
  ASSET_ALREADY_LOST: 'errAssetAlreadyLost',
  // AST-lost-report v2.0 — 4 mã mới (BE DD §7.1).
  RECOVERY_SCOPE_INVALID: 'errRecoveryScopeInvalid',
  ASSET_NOT_LOST: 'errAssetNotLost',
  ASSET_TAG_ALREADY_ATTACHED: 'errAssetTagAlreadyAttached',
  // v2.1 — distinct from the one above: the tracker is attached to NOTHING (opposite cause).
  TAG_NOT_ATTACHABLE: 'errTagNotAttachable',
  LOAN_ASSET_NOT_LOST: 'errLoanAssetNotLost',
  ASSET_NOT_ASSIGNEE: 'errAssetNotAssignee',
  ASSET_LOST_LOCKED: 'errAssetLostLocked',
  TAG_NOT_LOST: 'errTagNotLost',
  TAG_LOST: 'errTagLost',
  LOSS_EXPORT_TOO_LARGE: 'errLossExportTooLarge',
  // Department-move guards (asset/user relocation)
  ASSET_HAS_MANAGERS: 'errAssetHasManagers',
  USER_HAS_ACTIVE_LOANS: 'errUserHasActiveLoans',
  USER_HAS_MANAGED_ASSETS: 'errUserHasManagedAssets',
  ORG_HAS_ACTIVE_SHARED_LOANS: 'errOrgHasActiveSharedLoans',
  ORG_MANAGES_SHARED_ASSETS: 'errOrgManagesSharedAssets',
  ORG_HAS_ASSETS: 'errOrgHasAssets',
  NOTIF_NOT_FOUND: 'errNotifNotFound',
  // FAQ-kb (knowledge base)
  FAQ_NOT_FOUND: 'errFaqNotFound',
  FAQ_UNAVAILABLE: 'errFaqUnavailable',
  FAQ_IMAGE_TYPE_INVALID: 'errInvalidImageType',
  FAQ_IMAGE_TOO_LARGE: 'errFaqImageTooLarge',
  FAQ_IMAGE_UPLOAD_FAILED: 'errFaqImageUploadFailed',

  // ── Rà soát độ phủ 2026-09 (api-error.coverage.test.ts) ──────────────────────
  // 20 mã dưới đây backend đã trả từ lâu nhưng bảng này chưa có → toast rơi xuống
  // câu chung theo HTTP status. Xem docs/fixes/error-code-coverage.md.

  // Vòng đời user (màn Organization & User)
  CANNOT_DELETE_SELF: 'errCannotDeleteSelf',
  CANNOT_SET_OWN_PASSWORD: 'errCannotSetOwnPassword',
  USER_ALREADY_DELETED: 'errUserAlreadyDeleted',
  USER_NOT_DEACTIVATED: 'errUserNotDeactivated',
  // corp_admin phải quản lý ở màn Corp Admin, không phải ở đây — nói rõ lối đi.
  CANNOT_DEACTIVATE_CORP_ADMIN: 'errCannotDeactivateCorpAdmin',
  SELF_DEACTIVATE_ROLE_NOT_ALLOWED: 'errSelfDeactivateRoleNotAllowed',
  CORP_ADMIN_NOT_DEACTIVATED: 'errCorpAdminNotDeactivated',

  // Đổi mật khẩu (MyPage)
  AUTH_CURRENT_PASSWORD_INCORRECT: 'errCurrentPasswordIncorrect',
  PASSWORD_SAME_AS_OLD: 'errPasswordSameAsOld',

  // Phạm vi truy cập
  ACCESS_DENIED: 'errAccessDenied',
  ASSET_VIEW_NOT_ALLOWED: 'errAssetViewNotAllowed',
  ASSET_NOT_IN_SCOPE: 'errAssetNotInScope',

  // Master data
  CATEGORY_NOT_FOUND: 'errCategoryNotFound',
  DEVICE_NOT_FOUND: 'errDeviceNotFound',
  ALERT_SETTINGS_NOT_FOUND: 'errAlertSettingsNotFound',

  // Giám sát TTS
  TAG_ALREADY_DEACTIVATED: 'errTagAlreadyDeactivated',
  TTS_PARENT_INVALID: 'errTtsParentInvalid',
  TTS_RATE_LIMITED: 'errTtsRateLimited',
  TTS_TOKEN_SERVICE_NOT_CONFIGURED: 'errTtsTokenServiceNotConfigured',
  TTS_ACCOUNT_TENANT_MISMATCH: 'errTtsAccountTenantMismatch',
  TTS_CREDENTIAL_OVERRIDE_NOT_ALLOWED: 'errTtsCredentialOverrideNotAllowed',
}

/** Map a stable backend `code` directly to a localized message (fallback: the
 *  code itself). Used to render per-row CSV import errors (ORG-csv-import). */
export function messageForCode(code: string): string {
  const key = CODE_KEY[code]
  return key ? tr(key) : code
}

/** HTTP status → i18n key (fallback chung khi không có code cụ thể). */
function statusKey(status: number | undefined): string {
  if (status === 401 || status === 403) return 'errUnauthorized'
  if (status === 404) return 'errNotFound'
  if (status === 409) return 'errConflict'
  if (status === 400 || status === 422) return 'errValidation'
  if (status && status >= 500) return 'errServer'
  return 'errRequestFailed'
}

/** 1 phần tử `fields[]` trong RFC 9457 (lỗi validation theo field). */
interface ProblemField {
  field: string
  code: string
  message: string
}

/**
 * Gom lỗi field-level từ một lỗi API (RFC 9457 `fields[]`) thành map
 * `{ field: [message, ...] }` để gắn vào `AppFormField :errors`.
 *
 * `message` đã được backend localize theo `Accept-Language` (xem CLAUDE.COMMON
 * §1.2 — `fields[].message` là giá trị đã dịch), nên dùng thẳng. Trả `{}` nếu
 * lỗi không phải dạng field-level (vd lỗi business top-level, network…).
 */
export function fieldErrorsFromApiError(err: unknown): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  if (axios.isAxiosError(err)) {
    const fields = (err.response?.data as { fields?: ProblemField[] } | undefined)?.fields
    if (Array.isArray(fields)) {
      for (const f of fields) {
        if (!f?.field || !f?.message) continue
        ;(result[f.field] ??= []).push(f.message)
      }
    }
  }
  return result
}

/** Trả message lỗi ĐÃ LOCALIZE từ một lỗi bất kỳ (axios/Problem Details). */
export function apiErrorMessage(err: unknown): string {
  return tr(apiErrorKey(err))
}

/**
 * Trả i18n KEY của lỗi (KHÔNG resolve sang chuỗi) — để caller lưu key rồi render
 * qua `t(key)` của composable `useI18n`, nhờ đó thông báo lỗi ĐỔI THEO ngôn ngữ khi
 * user chuyển JA↔EN (chuỗi đã dịch sẵn thì không reactive). Xem MyPage.
 */
export function apiErrorKey(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status
    const body = err.response?.data as { code?: string } | undefined
    const codeKey = body?.code ? CODE_KEY[body.code] : undefined
    return codeKey ?? statusKey(status)
  }
  return 'errNetwork'
}
